import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listAppointments, type Appointment } from '../api/appointments.ts';
import { searchPatients } from '../api/patients.ts';
import { useSession } from '../auth/sessionContext.ts';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { Button, Panel, SelectField } from '../components/primitives.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDateHeading, localDateKey } from '../format/formatDateTime.ts';
import { useHasRoomForCalendarGrid } from '../hooks/useMediaQuery.ts';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { AppointmentFormDialog } from '../schedule/AppointmentFormDialog.tsx';
import { AppointmentList } from '../schedule/AppointmentList.tsx';
import { CalendarGrid, type CalendarColumn } from '../schedule/CalendarGrid.tsx';
import { NoShowSummaryPanel } from '../schedule/NoShowSummaryPanel.tsx';
import { WaitlistPanel } from '../schedule/WaitlistPanel.tsx';
import { useOrgMembers } from '../schedule/useOrgMembers.ts';
import { addDays, daysInWeek, weekContaining, weekRangeToQuery } from '../schedule/weekRange.ts';

/**
 * Book, reschedule, and manage appointments — one provider's week as a real calendar grid, or every
 * provider's schedule for one day at a time, per direct user feedback on the live demo, 2026-08-18:
 * "the calendar view needs to look like a calendar, not individual appointments. we want to use
 * provider name, not provider id on the calendar view. the physician should be able to see everyone's
 * schedule." This replaces the first pass's plain Monday-Sunday list of `Panel`s and its raw
 * "Provider ID" text field — that pass's own header explained both as deliberate, named gaps ("a list
 * over a calendar grid nobody had time to make responsive," "this backend still has no real
 * member/provider directory"); both are closed now, not worked around.
 *
 * **Two view modes, one query shape.** `selection` holds either a real provider's user id
 * (`mode === 'provider'`) or the `ALL_PROVIDERS` sentinel (`mode === 'all'`), and `providerId` below
 * is `undefined` in the latter case — `listAppointmentsQuerySchema` (`@emr/shared`) now accepts an
 * absent `providerId` and the server returns every appointment in the caller's own org for the range
 * instead of one provider's, so this is one query either way, never N single-provider queries from
 * the client.
 *
 * **A provider *directory* now exists** (`GET /api/orgs/members`, `useOrgMembers`), so both the
 * "Viewing" picker here and `AppointmentFormDialog`'s own provider field are a `<select>` of known
 * org members — `email (role)`, never a raw id — rather than a pasted UUID an independent review
 * once had to add a confirmation lookup to catch a typo in.
 *
 * **Navigation is week-at-a-time for a single provider's grid, day-at-a-time everywhere else.** A
 * 7-day time grid makes sense for one provider; cramming every provider into a 7-day grid at once
 * does not, so the "all providers" view is provider-columns for one day, navigated a day at a time —
 * and the same day-at-a-time navigation is reused for the phone-width agenda fallback below, since a
 * single day is exactly what an agenda list shows regardless of which mode picked it. `anchor` is the
 * one date state both navigation styles move: the week nav shifts it by 7 days, the day nav by 1.
 *
 * **Responsive from the start, not a grid nobody had time to fix.** `useHasRoomForCalendarGrid`
 * (`hooks/useMediaQuery.ts`) governs a real content swap, not a squeeze: at or above its breakpoint,
 * `CalendarGrid` renders a real time-grid (day columns for one provider, or provider columns for one
 * day); below it, the same data renders as `AppointmentList`'s already-responsive agenda list — one
 * day's appointments for a single provider, or one stacked panel per provider for "all providers" —
 * with the same day-switcher nav either way. Verified live in a real disposable headless Chrome
 * instance at phone and desktop widths, light and dark, per this project's own mobile-first
 * discipline (`claude-kit/rules/invariants.md`).
 *
 * **Gated the same way `AnalyticsPage`/`AuditPage` are** (`GatedPages.tsx`'s own header on why): the
 * nav can hide the `/schedule` link, but a typed URL bypasses the nav entirely, so the route itself
 * has to re-run the same capability check. Splitting the gate wrapper (`SchedulePage`) from the
 * actual content (`ScheduleContent`) matters beyond style here — `ScheduleContent`'s hooks (the
 * appointments query in particular) must not run at all for a viewer `GatedRoute` is about to
 * refuse, and a hook only avoids running when the component that owns it is never rendered, not
 * when its JSX merely sits unused inside a conditional.
 */
export function SchedulePage() {
  const { t } = useTranslation('schedule');
  return (
    <GatedRoute title={t('page.title')} capability="schedule:manage" feature={null}>
      <ScheduleContent />
    </GatedRoute>
  );
}

/** The "Viewing" select's value when no single provider is chosen — distinct from any real user id
 * (a UUID always contains hyphens in fixed positions; this never will). */
const ALL_PROVIDERS = 'all';

function ScheduleContent() {
  const { t } = useTranslation('schedule');
  const session = useSession();
  const { preference } = useDateTimeFormat();
  const members = useOrgMembers();
  const hasRoomForGrid = useHasRoomForCalendarGrid();

  const [selection, setSelection] = useState<string>(session.userId);
  const mode: 'provider' | 'all' = selection === ALL_PROVIDERS ? 'all' : 'provider';
  const providerId = mode === 'provider' ? selection : undefined;
  // Day-at-a-time navigation for "all providers" (there is no week-wide grid shape for it — see this
  // file's own header) and for the phone agenda fallback regardless of mode; week-at-a-time only for
  // a single provider's desktop grid.
  const useDayNav = mode === 'all' || !hasRoomForGrid;

  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const week = weekContaining(anchor);
  const { from, to } = weekRangeToQuery(week);
  const anchorDayKey = localDateKey(anchor.toISOString());

  const appointments = useQuery({
    queryKey: ['appointments', mode, providerId, from, to],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      listAppointments({ providerId, from, to }, signal),
  });

  // K5: batch-prefetch every distinct patient this range's appointments reference, once, instead of
  // `AppointmentList`'s/`CalendarGrid`'s own per-row `usePatient` calls each firing their own
  // `GET /api/patients/:patientId` — a week with 20-30 distinct patients otherwise means 20-30
  // concurrent single-record requests (`usePatient.ts`'s own header names this exactly). Populating
  // the cache under the same `['patient', id]` key `usePatient` already reads means those existing
  // per-row call sites need no change at all — they simply become cache hits.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (appointments.data === undefined || appointments.data.length === 0) return;
    const ids = [...new Set(appointments.data.map((appointment) => appointment.patientId))];
    const controller = new AbortController();
    searchPatients({ ids }, controller.signal)
      .then((patients) => {
        for (const patient of patients) {
          queryClient.setQueryData(['patient', patient.id], patient);
        }
      })
      .catch(() => {
        // Swallowed deliberately: this is a cache-warming prefetch, not the data path itself. A
        // failure here just means AppointmentList's/CalendarGrid's own usePatient calls fall back to
        // their normal per-row fetch, the same as before this batching existed — not a user-facing
        // error of its own.
      });
    return () => {
      controller.abort();
    };
  }, [appointments.data, queryClient]);

  const [dialog, setDialog] = useState<{
    readonly open: boolean;
    // `| undefined` explicit, not just `?:` — see `AppointmentFormDialog`'s own prop of the same
    // shape for why `exactOptionalPropertyTypes` needs it stated rather than implied.
    readonly appointment?: Appointment | undefined;
  }>({
    open: false,
  });

  const byDay = new Map<string, Appointment[]>();
  if (appointments.data !== undefined) {
    for (const appointment of appointments.data) {
      const key = localDateKey(appointment.startsAt);
      const existing = byDay.get(key);
      if (existing !== undefined) {
        existing.push(appointment);
      } else {
        byDay.set(key, [appointment]);
      }
    }
  }
  const selectedDayAppointments = byDay.get(anchorDayKey) ?? [];

  const lastDayOfWeek = addDays(week.to, -1);
  // `selection` (not the possibly-`undefined` `providerId` above) is what's known to be a real user
  // id here — the dialog's own provider picker seeds from this but is never restricted to it.
  const bookingDefaultProviderId = mode === 'provider' ? selection : session.userId;

  function openReschedule(appointment: Appointment) {
    setDialog({ open: true, appointment });
  }

  const gridColumns: readonly CalendarColumn[] =
    mode === 'provider'
      ? daysInWeek(week).map((day) => {
          const iso = day.toISOString();
          const key = localDateKey(iso);
          return {
            key,
            heading: formatDateHeading(iso, preference),
            appointments: byDay.get(key) ?? [],
          };
        })
      : (members.data ?? []).map((member) => ({
          key: member.userId,
          heading: `${member.email} (${member.role})`,
          appointments: selectedDayAppointments.filter(
            (appointment) => appointment.providerId === member.userId,
          ),
        }));

  return (
    <>
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setDialog({ open: true, appointment: undefined });
          }}
        >
          {t('page.bookAppointment')}
        </Button>
      </div>

      <Panel title={t('page.viewingPanelTitle')}>
        <div className="flex flex-wrap items-end gap-3">
          {members.data === undefined ? (
            <p className="m-0 text-sm text-ink-muted">
              {members.isError ? t('page.couldNotLoadProviders') : t('page.loadingProviders')}
            </p>
          ) : (
            <SelectField
              label={t('page.providerLabel')}
              value={selection}
              onChange={(event) => {
                setSelection(event.target.value);
              }}
              className="max-w-md flex-1"
            >
              <option value={ALL_PROVIDERS}>{t('page.allProviders')}</option>
              {members.data.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.email} ({member.role}){member.userId === session.userId ? ' — you' : ''}
                </option>
              ))}
            </SelectField>
          )}
          {selection !== session.userId ? (
            <Button
              variant="secondary"
              onClick={() => {
                setSelection(session.userId);
              }}
            >
              {t('page.mySchedule')}
            </Button>
          ) : null}
        </div>
      </Panel>

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setAnchor((current) => addDays(current, useDayNav ? -1 : -7));
              }}
            >
              {useDayNav ? t('page.previousDay') : t('page.previousWeek')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAnchor(new Date());
              }}
            >
              {useDayNav ? t('page.today') : t('page.thisWeek')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAnchor((current) => addDays(current, useDayNav ? 1 : 7));
              }}
            >
              {useDayNav ? t('page.nextDay') : t('page.nextWeek')}
            </Button>
          </div>
          <p className="m-0 text-sm text-ink-muted">
            {useDayNav ? (
              formatDateHeading(anchor.toISOString(), preference)
            ) : (
              <>
                {formatDateHeading(week.from.toISOString(), preference)} –{' '}
                {formatDateHeading(lastDayOfWeek.toISOString(), preference)}
              </>
            )}
          </p>
        </div>
      </Panel>

      {appointments.isLoading ? (
        <LoadingNotice label={t('page.loadingSchedule')} />
      ) : appointments.isError ? (
        <FailureNotice
          error={appointments.error}
          onRetry={() => {
            void appointments.refetch();
          }}
        />
      ) : hasRoomForGrid ? (
        <Panel>
          <CalendarGrid columns={gridColumns} onSelect={openReschedule} />
        </Panel>
      ) : mode === 'provider' ? (
        <Panel title={formatDateHeading(anchor.toISOString(), preference)}>
          <AppointmentList appointments={selectedDayAppointments} onReschedule={openReschedule} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {(members.data ?? []).map((member) => (
            <Panel key={member.userId} title={`${member.email} (${member.role})`}>
              <AppointmentList
                appointments={selectedDayAppointments.filter(
                  (appointment) => appointment.providerId === member.userId,
                )}
                onReschedule={openReschedule}
              />
            </Panel>
          ))}
        </div>
      )}

      <AppointmentFormDialog
        open={dialog.open}
        onOpenChange={(open) => {
          setDialog((current) => ({ ...current, open }));
        }}
        defaultProviderId={bookingDefaultProviderId}
        appointment={dialog.appointment}
        onSaved={() => undefined}
      />

      {/* Waitlists and no-show/cancellation reporting (`BACKLOG.md` K3) — additive to the calendar/
          agenda above, not a replacement for any of it. Always rendered below the main schedule view
          rather than behind a tab, since both are meant to be a quick glance a front-desk user reaches
          without leaving the page they are already working from. */}
      <WaitlistPanel />
      <NoShowSummaryPanel />
    </>
  );
}
